import React from 'react';
import { Trash2, X, Plus } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import type {
  FormField,
  FormPage,
  FieldRule,
  RuleCondition,
  RuleAction,
  RuleOperator,
  CalculationOperation,
  ScoreVariable,
} from '../../../types/forms';
import { NON_INPUT_FIELD_TYPES, normalizeFieldOption } from '../../../types/forms';
import {
  createDefaultRule,
  createDefaultCondition,
  createDefaultJumpAction,
  createDefaultCalcAction,
} from '../../../lib/ruleUtils';

// =====================================================
// CONSTANTS
// =====================================================

const CONDITION_TYPE_OPTIONS = [
  { value: 'if', label: 'Se' },
  { value: 'always', label: 'Sempre' },
];

const OPERATOR_OPTIONS = [
  { value: 'is', label: 'igual a' },
  { value: 'is_not', label: 'diferente de' },
];

const ACTION_GROUPS = [
  {
    label: 'Lógica',
    options: [{ value: 'jump_to', label: 'Ir para' }],
  },
  {
    label: 'Cálculos',
    options: [
      { value: 'add', label: 'Somar' },
      { value: 'subtract', label: 'Subtrair' },
      { value: 'multiply', label: 'Multiplicar' },
      { value: 'divide', label: 'Dividir' },
    ],
  },
];

// =====================================================
// PROPS
// =====================================================

interface RuleEditorProps {
  field: FormField;
  allFields: FormField[];
  scoringVariables: ScoreVariable[];
  onUpdateRules: (fieldId: string, rules: FieldRule[]) => void;
  /** Form pages — when provided, jump targets include both pages and fields (Story 5.7). */
  pages?: FormPage[];
}

// =====================================================
// HELPERS
// =====================================================

function getFieldOptions(field: FormField | undefined): { value: string; label: string }[] {
  if (!field?.options?.length) return [];
  return field.options.map(opt => {
    const normalized = normalizeFieldOption(opt);
    return { value: normalized.value, label: normalized.label };
  });
}

function getActionTypeValue(action: RuleAction): string {
  if (action.type === 'jump_to' || action.type === 'jump_to_page') return 'jump_to';
  return action.operation;
}

function getSelectableFields(allFields: FormField[]): { value: string; label: string }[] {
  return allFields
    .filter(f => !NON_INPUT_FIELD_TYPES.includes(f.type))
    .map((f, i) => ({ value: f.id, label: `${i + 1}. ${f.label}` }));
}

function getAllFieldsAsTargets(allFields: FormField[]): { value: string; label: string }[] {
  return allFields.map((f, i) => ({ value: f.id, label: `${i + 1}. ${f.label}` }));
}

// Story 5.7: hybrid target picker — pages + fields. Option values are prefixed
// (page:<id> | field:<id>) so the consumer can route to the correct action variant.
const PAGE_PREFIX = 'page:';
const FIELD_PREFIX = 'field:';

function getHybridTargetGroups(
  allFields: FormField[],
  pages: FormPage[]
): { label: string; options: { value: string; label: string }[] }[] {
  const groups: { label: string; options: { value: string; label: string }[] }[] = [];

  const sortedPages = [...pages].sort((a, b) => a.order - b.order);
  const fieldsByPage = new Map<string, FormField[]>();
  allFields.forEach((f) => {
    if (!f.pageId) return;
    const list = fieldsByPage.get(f.pageId) || [];
    list.push(f);
    fieldsByPage.set(f.pageId, list);
  });

  if (sortedPages.length > 0) {
    groups.push({
      label: 'Páginas',
      options: sortedPages.map((p, i) => ({
        value: `${PAGE_PREFIX}${p.id}`,
        label: `📄 ${p.title?.trim() || `Página ${i + 1}`}`,
      })),
    });
  }

  // Fields — show page context when available
  const pageNameById = new Map<string, string>();
  sortedPages.forEach((p, i) => pageNameById.set(p.id, p.title?.trim() || `Página ${i + 1}`));

  groups.push({
    label: 'Campos',
    options: allFields.map((f, i) => {
      const pageName = f.pageId ? pageNameById.get(f.pageId) : undefined;
      const suffix = pageName ? ` (em ${pageName})` : '';
      return {
        value: `${FIELD_PREFIX}${f.id}`,
        label: `  ↳ ${i + 1}. ${f.label}${suffix}`,
      };
    }),
  });

  return groups;
}

function actionToTargetValue(action: RuleAction): string {
  if (action.type === 'jump_to_page') return `${PAGE_PREFIX}${action.targetPageId}`;
  if (action.type === 'jump_to') return `${FIELD_PREFIX}${action.targetFieldId}`;
  return '';
}

function targetValueToAction(value: string): { type: 'jump_to'; targetFieldId: string } | { type: 'jump_to_page'; targetPageId: string } | null {
  if (value.startsWith(PAGE_PREFIX)) {
    return { type: 'jump_to_page', targetPageId: value.slice(PAGE_PREFIX.length) };
  }
  if (value.startsWith(FIELD_PREFIX)) {
    return { type: 'jump_to', targetFieldId: value.slice(FIELD_PREFIX.length) };
  }
  return null;
}

// =====================================================
// RULE CONDITION ROW
// =====================================================

function RuleConditionRow({
  condition,
  allFields,
  onUpdate,
  onRemove,
  canRemove,
}: {
  condition: RuleCondition;
  allFields: FormField[];
  onUpdate: (updated: RuleCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const sourceField = allFields.find(f => f.id === condition.fieldId);
  const fieldOptions = getFieldOptions(sourceField);
  const selectableFields = getSelectableFields(allFields);
  // Show dropdown only when the current value matches an existing option (or is empty).
  // If the stored value doesn't match any option (e.g. data imported via API), fall back to text input.
  const valueStr = typeof condition.value === 'string' ? condition.value : '';
  const valueMatchesOption = !valueStr || fieldOptions.some(o => o.value === valueStr);
  const showDropdown = fieldOptions.length > 0 && valueMatchesOption;

  return (
    <div className="flex flex-col gap-1.5 p-2 rounded-lg bg-neutral-900/30">
      {/* Field selector */}
      <Select
        value={condition.fieldId}
        onChange={(e) => onUpdate({ ...condition, fieldId: e.target.value, value: '' })}
        options={selectableFields}
        placeholder="Selecione o campo..."
        className="text-xs"
      />

      <div className="flex gap-1.5 items-start">
        {/* Operator */}
        <Select
          value={condition.operator}
          onChange={(e) => onUpdate({ ...condition, operator: e.target.value as RuleOperator })}
          options={OPERATOR_OPTIONS}
          className="text-xs w-32 flex-shrink-0"
        />

        {/* Value */}
        <div className="flex-1">
          {showDropdown ? (
            <Select
              value={valueStr}
              onChange={(e) => onUpdate({ ...condition, value: e.target.value })}
              options={fieldOptions}
              placeholder="Selecione..."
              className="text-xs"
            />
          ) : (
            <Input
              value={valueStr}
              onChange={(e) => onUpdate({ ...condition, value: e.target.value })}
              placeholder="Digite o valor..."
              className="text-xs"
            />
          )}
        </div>

        {/* Remove */}
        {canRemove && (
          <button
            onClick={onRemove}
            className="p-1.5 text-neutral-500 hover:text-red-400 flex-shrink-0"
            title="Remover condição"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// =====================================================
// RULE ACTION ROW
// =====================================================

function RuleActionRow({
  action,
  allFields,
  scoringVariables,
  pages,
  onUpdate,
}: {
  action: RuleAction;
  allFields: FormField[];
  scoringVariables: ScoreVariable[];
  pages?: FormPage[];
  onUpdate: (updated: RuleAction) => void;
}) {
  const currentValue = getActionTypeValue(action);
  const hasPages = !!pages && pages.length > 0;
  const fieldTargetOptions = getAllFieldsAsTargets(allFields);
  const hybridGroups = hasPages ? getHybridTargetGroups(allFields, pages!) : [];
  const variableOptions = scoringVariables.map(v => ({ value: v.id, label: v.name }));

  const handleActionTypeChange = (newValue: string) => {
    if (newValue === 'jump_to') {
      onUpdate(createDefaultJumpAction(allFields[0]?.id || ''));
    } else {
      const op = newValue as CalculationOperation;
      onUpdate(createDefaultCalcAction(op, scoringVariables[0]?.id || ''));
    }
  };

  const handleHybridTargetChange = (value: string) => {
    const next = targetValueToAction(value);
    if (next) onUpdate(next);
  };

  const isJumpAction = action.type === 'jump_to' || action.type === 'jump_to_page';

  return (
    <div className="space-y-1.5">
      <p className="text-xs text-neutral-500 font-medium">Então</p>
      <div className="flex flex-col gap-1.5 p-2 rounded-lg bg-neutral-900/30">
        {/* Action type selector (grouped) */}
        <Select
          value={currentValue}
          onChange={(e) => handleActionTypeChange(e.target.value)}
          options={[]}
          groups={ACTION_GROUPS}
          className="text-xs"
        />

        {/* Action params */}
        {isJumpAction && hasPages && (
          <Select
            value={actionToTargetValue(action)}
            onChange={(e) => handleHybridTargetChange(e.target.value)}
            options={[]}
            groups={hybridGroups}
            placeholder="Selecione o destino..."
            className="text-xs"
          />
        )}
        {isJumpAction && !hasPages && action.type === 'jump_to' && (
          <Select
            value={action.targetFieldId}
            onChange={(e) => onUpdate({ ...action, targetFieldId: e.target.value })}
            options={fieldTargetOptions}
            placeholder="Selecione o destino..."
            className="text-xs"
          />
        )}

        {action.type === 'calculation' && (
          <div className="flex gap-1.5 items-center">
            <Input
              type="number"
              value={String(action.value)}
              onChange={(e) => onUpdate({ ...action, value: Number(e.target.value) })}
              className="text-xs w-20 flex-shrink-0"
            />
            <span className="text-xs text-neutral-500 flex-shrink-0">para</span>
            {variableOptions.length > 0 ? (
              <Select
                value={action.variableId}
                onChange={(e) => onUpdate({ ...action, variableId: e.target.value })}
                options={variableOptions}
                className="text-xs flex-1"
              />
            ) : (
              <span className="text-xs text-neutral-600 italic">Nenhuma variável</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================
// RULE CARD
// =====================================================

function RuleCard({
  rule,
  ruleIndex,
  field,
  allFields,
  scoringVariables,
  pages,
  onUpdateRule,
  onDeleteRule,
}: {
  rule: FieldRule;
  ruleIndex: number;
  field: FormField;
  allFields: FormField[];
  scoringVariables: ScoreVariable[];
  pages?: FormPage[];
  onUpdateRule: (updated: FieldRule) => void;
  onDeleteRule: () => void;
}) {
  // --- Condition handlers ---
  const handleConditionTypeChange = (newType: string) => {
    const updated: FieldRule = {
      ...rule,
      conditionType: newType as 'if' | 'always',
      conditions: newType === 'always' ? [] : (rule.conditions.length > 0 ? rule.conditions : [createDefaultCondition(field.id)]),
    };
    onUpdateRule(updated);
  };

  const handleUpdateCondition = (condIndex: number, updated: RuleCondition) => {
    const newConditions = rule.conditions.map((c, i) => i === condIndex ? updated : c);
    onUpdateRule({ ...rule, conditions: newConditions });
  };

  const handleRemoveCondition = (condIndex: number) => {
    onUpdateRule({ ...rule, conditions: rule.conditions.filter((_, i) => i !== condIndex) });
  };

  const handleAddCondition = () => {
    onUpdateRule({
      ...rule,
      conditions: [...rule.conditions, createDefaultCondition(field.id)],
    });
  };

  // --- Action handler ---
  const handleUpdateAction = (actionIndex: number, updated: RuleAction) => {
    const newActions = rule.actions.map((a, i) => i === actionIndex ? updated : a);
    onUpdateRule({ ...rule, actions: newActions });
  };

  // v1: single action per rule — actions array supports future multi-action rules
  const currentAction = rule.actions[0] || createDefaultJumpAction(allFields[0]?.id || field.id);

  return (
    <div className="rounded-xl border border-neutral-700/50 bg-neutral-900/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-700/30">
        <span className="text-xs text-neutral-400 font-medium">Regra {ruleIndex + 1}</span>
        <button
          onClick={onDeleteRule}
          className="flex items-center gap-1 text-xs text-red-400/70 hover:text-red-400 transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          Excluir regra
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* Condition type */}
        <Select
          value={rule.conditionType}
          onChange={(e) => handleConditionTypeChange(e.target.value)}
          options={CONDITION_TYPE_OPTIONS}
          className="text-xs"
        />

        {/* Conditions (only when "if") */}
        {rule.conditionType === 'if' && (
          <div className="space-y-2">
            {rule.conditions.map((cond, condIdx) => (
              <RuleConditionRow
                key={cond.id}
                condition={cond}
                allFields={allFields}
                onUpdate={(updated) => handleUpdateCondition(condIdx, updated)}
                onRemove={() => handleRemoveCondition(condIdx)}
                canRemove={rule.conditions.length > 1}
              />
            ))}

            <button
              onClick={handleAddCondition}
              className="w-full py-1.5 text-xs text-neutral-500 hover:text-neutral-300 border border-dashed border-neutral-700/50 rounded-lg hover:border-neutral-600 transition-colors"
            >
              + Adicionar condição
            </button>
          </div>
        )}

        {/* Action */}
        <RuleActionRow
          action={currentAction}
          allFields={allFields}
          scoringVariables={scoringVariables}
          pages={pages}
          onUpdate={(updated) => handleUpdateAction(0, updated)}
        />
      </div>
    </div>
  );
}

// =====================================================
// RULE EDITOR (MAIN)
// =====================================================

export function RuleEditor({
  field,
  allFields,
  scoringVariables,
  onUpdateRules,
  pages,
}: RuleEditorProps) {
  const rules = field.rules || [];

  const handleUpdateRule = (ruleIndex: number, updated: FieldRule) => {
    const newRules = rules.map((r, i) => i === ruleIndex ? updated : r);
    onUpdateRules(field.id, newRules);
  };

  const handleDeleteRule = (ruleIndex: number) => {
    onUpdateRules(field.id, rules.filter((_, i) => i !== ruleIndex));
  };

  const handleAddRule = () => {
    const newRule = createDefaultRule(field.id);
    onUpdateRules(field.id, [...rules, newRule]);
  };

  return (
    <div className="pt-3 border-t border-neutral-700/50">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-neutral-500 uppercase">Regras</p>
        <span className="text-xs text-neutral-600">{rules.length}</span>
      </div>

      <div className="space-y-2">
        {rules.map((rule, idx) => (
          <RuleCard
            key={rule.id}
            rule={rule}
            ruleIndex={idx}
            field={field}
            allFields={allFields}
            scoringVariables={scoringVariables}
            pages={pages}
            onUpdateRule={(updated) => handleUpdateRule(idx, updated)}
            onDeleteRule={() => handleDeleteRule(idx)}
          />
        ))}

        <button
          onClick={handleAddRule}
          className="w-full p-2 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors flex items-center justify-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Adicionar regra
        </button>
      </div>
    </div>
  );
}
